// Third party libs/components
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import InputMask from 'react-input-mask';
import * as Styled from './BillingAddress.styles';

// Components
import CreditCardInput from '@components/CreditCardInput';

// Utils
import { useCurrentLang, translate as t } from '@utils/translate';
import { getCountries } from '@utils/country';

const BillingAddress = ({
  initialAddress = null,
  address,
  title,
  subtitle,
  isDisabled,
  onCancel,
  onSave,
  theme = 'default',
  onChange,
}) => {
  const lang = useCurrentLang();
  const maskProps = {
    maskChar: '',
    alwaysShowMask: false,
  };
  const [countryList, setCountryList] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [addressData, setAdressData] = useState(initialAddress);
  const [mask] = useState({
    stateName: 'aa',
  });
  const TEXTS = {
    BACK: t('back'),
    CONFIRM: t('confirmButton'),
    REFERENCE_NAME: t('referenceName'),
    SELECT: t('selectSomething'),
    CEP: t('zipCode'),
    STREET: t('street'),
    CITY: t('city'),
    STATE: t('state'),
    NUMBER: t('number'),
    COMPLEMENT: t('complement'),
  };

  function handleWithCountry() {
    if (address) {
      const countryLang = lang.split('_')[0] || 'pt';
      const country = getCountries(countryLang).find(
        country => country?.code === address?.countryName
      );

      setAdressData(oldState => {
        return {
          ...oldState,
          countryName: country?.name,
        };
      });
    }
  }

  useEffect(() => {
    const countryLang = lang.split('_')[0] || 'pt';
    const countries = getCountries(countryLang);

    setCountryList(countries);
    handleWithCountry();

    return () => {
      setAdressData(null);
    };
  }, []);

  useEffect(() => {
    if (address) {
      setAdressData(address);
      handleWithCountry();
    }
  }, [address]);

  useEffect(() => {
    if (onChange) {
      onChange(addressData);
    }
  }, [addressData]);

  function handleChange(evt) {
    const { name, value } = evt.target;

    setAdressData(oldState => ({
      ...oldState,
      [name]: value,
    }));
  }

  function handleCancel() {
    onCancel();
  }

  function handleSave() {
    onSave(addressData);
  }

  function validateForm() {
    const numOfInputs = 7;
    const entries = Object.entries(addressData || {});
    let isFormValid = false;

    if (entries.length >= numOfInputs) {
      isFormValid = true;

      entries.forEach(([name, value]) => {
        if (name !== 'complement' && (!value || value.trim() === '')) {
          isFormValid = false;
        }
      });
    }

    setIsFormValid(isFormValid);
  }

  return (
    <Styled.Container theme={theme}>
      <Styled.Title>{title}</Styled.Title>
      <Styled.Subtitle>{subtitle}</Styled.Subtitle>
      <Styled.Form isDisabled={isDisabled}>
        <Styled.InputRow>
          <CreditCardInput>
            <input
              tabIndex="6"
              name="name"
              type="text"
              value={addressData?.name ? addressData.name : ''}
              placeholder=" "
              maxLength="25"
              onChange={evt => handleChange(evt)}
              onKeyUp={validateForm}
            />
            <span>{TEXTS.REFERENCE_NAME}</span>
          </CreditCardInput>
        </Styled.InputRow>

        <Styled.InputRow>
          <Styled.InputColumn size={'30%'}>
            <CreditCardInput>
              <select
                onSelect={validateForm}
                name="countryName"
                tabIndex="7"
                onChange={evt => handleChange(evt)}
              >
                <option defaultValue="">
                  {addressData?.countryName || TEXTS.SELECT}
                </option>
                {countryList &&
                  countryList.map(({ code, name }) => {
                    return (
                      <option value={code} key={code}>
                        {name}
                      </option>
                    );
                  })}
              </select>
              <span>{TEXTS.COUNTRY}</span>
            </CreditCardInput>
          </Styled.InputColumn>
          <Styled.InputColumn size={'67%'}>
            <CreditCardInput>
              <input
                tabIndex="8"
                name="zipCode"
                value={addressData?.zipCode ? addressData.zipCode : ''}
                type="text"
                placeholder=" "
                maxLength="25"
                onChange={evt => handleChange(evt)}
                onKeyUp={validateForm}
              />
              <span>{TEXTS.CEP}</span>
            </CreditCardInput>
          </Styled.InputColumn>
        </Styled.InputRow>
        <Styled.InputRow>
          <CreditCardInput>
            <input
              tabIndex="9"
              name="address"
              value={addressData?.address ? addressData.address : ''}
              type="text"
              placeholder=" "
              maxLength="100"
              onChange={evt => handleChange(evt)}
              onKeyUp={validateForm}
            />
            <span>{TEXTS.STREET}</span>
          </CreditCardInput>
        </Styled.InputRow>
        <Styled.InputRow>
          <Styled.InputColumn size={'30%'}>
            <CreditCardInput>
              <input
                tabIndex="10"
                name="number"
                value={addressData?.number ? addressData.number : ''}
                type="text"
                placeholder=" "
                maxLength="25"
                onChange={evt => handleChange(evt)}
                onKeyUp={validateForm}
              />
              <span>{TEXTS.NUMBER}</span>
            </CreditCardInput>
          </Styled.InputColumn>
          <Styled.InputColumn size={'67%'}>
            <CreditCardInput>
              <input
                tabIndex="11"
                name="complement"
                value={addressData?.complement ? addressData.complement : ''}
                type="text"
                placeholder=" "
                maxLength="25"
                onChange={evt => handleChange(evt)}
                onKeyUp={validateForm}
              />
              <span>{TEXTS.COMPLEMENT}</span>
            </CreditCardInput>
          </Styled.InputColumn>
        </Styled.InputRow>
        <Styled.InputRow>
          <Styled.InputColumn size={'67%'}>
            <CreditCardInput>
              <input
                tabIndex="12"
                name="cityName"
                value={addressData?.cityName ? addressData.cityName : ''}
                type="text"
                placeholder=" "
                maxLength="25"
                onChange={evt => handleChange(evt)}
                onKeyUp={validateForm}
              />
              <span>{TEXTS.CITY}</span>
            </CreditCardInput>
          </Styled.InputColumn>
          <Styled.InputColumn size={'30%'}>
            <CreditCardInput>
              <InputMask
                style={{ textTransform: 'uppercase' }}
                {...maskProps}
                mask={mask.stateName}
                tabIndex="13"
                name="stateName"
                value={addressData?.stateName ? addressData.stateName : ''}
                type="text"
                placeholder=" "
                onChange={evt => handleChange(evt)}
                onKeyUp={validateForm}
              />
              <span>{TEXTS.STATE}</span>
            </CreditCardInput>
          </Styled.InputColumn>
        </Styled.InputRow>
      </Styled.Form>
      <Styled.ButtonContainer theme={theme}>
        <Styled.ButtonCancel type="button" onClick={handleCancel}>
          {TEXTS.BACK}
        </Styled.ButtonCancel>
        <Styled.ButtonConfirm
          theme={isFormValid || isDisabled ? 'active' : 'disabled'}
          onClick={handleSave}
          data-test="address.submit"
          type="button"
        >
          {TEXTS.CONFIRM}
        </Styled.ButtonConfirm>
      </Styled.ButtonContainer>
    </Styled.Container>
  );
};

BillingAddress.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  initialAddress: PropTypes.object,
  address: PropTypes.object,
  theme: PropTypes.string,
  isDisabled: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onChange: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
};

BillingAddress.defaultProps = {
  onChange: false,
};

const mapStateToProps = state => ({
  address: state.cart.selectedBillingAddress,
});

export default connect(mapStateToProps)(BillingAddress);
